import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUserProfileStore } from '@/lib/store/userProfileStore';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { Switch } from '@/components/common/Switch';
import Alert from '@/components/common/Alert';

const notificationPreferencesSchema = z.object({
  email_notifications: z.boolean(),
  schedule_updates: z.boolean(),
  shift_trade_requests: z.boolean(),
  reminder_notifications: z.boolean(),
  advance_reminder_days: z.number()
    .int()
    .min(0, { message: "Days must be 0 or greater" })
    .max(30, { message: "Days cannot exceed 30" })
});

type NotificationPreferencesFormValues = z.infer<typeof notificationPreferencesSchema>;

export const UserNotificationPreferencesForm = () => {
  const { userProfile, isLoading, error, fetchUserProfile, updateNotificationPreferences } = useUserProfileStore();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<NotificationPreferencesFormValues>({
    resolver: zodResolver(notificationPreferencesSchema),
    defaultValues: {
      email_notifications: true,
      schedule_updates: true,
      shift_trade_requests: true,
      reminder_notifications: true,
      advance_reminder_days: 3
    }
  });
  
  // For toggling switches
  const emailNotifications = watch('email_notifications');
  const scheduleUpdates = watch('schedule_updates');
  const shiftTradeRequests = watch('shift_trade_requests');
  const reminderNotifications = watch('reminder_notifications');
  
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);
  
  useEffect(() => {
    if (userProfile?.notification_preferences) {
      const prefs = userProfile.notification_preferences;
      setValue('email_notifications', prefs.email_notifications);
      setValue('schedule_updates', prefs.schedule_updates);
      setValue('shift_trade_requests', prefs.shift_trade_requests);
      setValue('reminder_notifications', prefs.reminder_notifications);
      setValue('advance_reminder_days', prefs.advance_reminder_days);
    }
  }, [userProfile, setValue]);
  
  const onSubmit = async (data: NotificationPreferencesFormValues) => {
    setIsSubmitting(true);
    setSuccessMessage(null);
    
    try {
      await updateNotificationPreferences(data);
      setSuccessMessage('Notification preferences updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading && !userProfile) {
    return (
      <Card className="p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
      
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Email Notifications</h3>
              <p className="text-sm text-gray-500">
                Receive notifications via email
              </p>
            </div>
            <Switch
              checked={emailNotifications}
              onChange={() => setValue('email_notifications', !emailNotifications)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Schedule Updates</h3>
              <p className="text-sm text-gray-500">
                Receive notifications about schedule changes
              </p>
            </div>
            <Switch
              checked={scheduleUpdates}
              onChange={() => setValue('schedule_updates', !scheduleUpdates)}
              disabled={!emailNotifications}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Shift Trade Requests</h3>
              <p className="text-sm text-gray-500">
                Receive notifications about shift trade requests
              </p>
            </div>
            <Switch
              checked={shiftTradeRequests}
              onChange={() => setValue('shift_trade_requests', !shiftTradeRequests)}
              disabled={!emailNotifications}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Shift Reminders</h3>
              <p className="text-sm text-gray-500">
                Receive reminders about upcoming shifts
              </p>
            </div>
            <Switch
              checked={reminderNotifications}
              onChange={() => setValue('reminder_notifications', !reminderNotifications)}
              disabled={!emailNotifications}
            />
          </div>
          
          <Input
            type="number"
            label="Advance Reminder (Days)"
            {...register('advance_reminder_days', { valueAsNumber: true })}
            error={errors.advance_reminder_days?.message}
            helperText="Days before your shift to send a reminder"
            disabled={!emailNotifications || !reminderNotifications}
            required
          />
        </div>
        
        <div className="pt-4 border-t flex justify-end">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={!emailNotifications}
          >
            Save Preferences
          </Button>
        </div>
      </form>
    </Card>
  );
}; 